import PageBreadcrumb from '../components/PageBreadcrumb';
import { useEmoji } from '../context/EmojiContext';
import '../components/DataPage.css';
import './Help.css';

const SECTIONS = [
  { id: 'get-started', title: 'Get started' },
  { id: 'email-providers', title: 'Email providers' },
  { id: 'csv-templates', title: 'CSV templates' },
  { id: 'extension', title: 'Browser extension' },
  { id: 'send-emails', title: 'Send emails' },
  { id: 'scheduled-logs', title: 'Scheduled emails & logs' },
];

const CSV_TEMPLATES = [
  {
    id: 'ports',
    label: 'Ports',
    filename: 'ports-import-template.csv',
    content: ['name,city', 'Nhava Sheva (JNPT),Navi Mumbai', 'Mundra Port,Mundra', 'Chennai Port,Chennai'].join(
      '\n'
    ),
  },
  {
    id: 'vessels',
    label: 'Vessels',
    filename: 'vessels-import-template.csv',
    content: ['name,email', 'MV Sea Pearl,vessel1@example.com', 'MT Ocean Glory,vessel2@example.com'].join('\n'),
  },
  {
    id: 'emails',
    label: 'Emails (senders)',
    filename: 'emails-import-template.csv',
    content: [
      'email,password,provider,display_name',
      'you@gmail.com,your-app-password,gmail,Balaji Marine',
      'office@yourdomain.com,your-mailbox-password,godaddy,Office Sales',
    ].join('\n'),
  },
  {
    id: 'recipients',
    label: 'Send Email (recipients)',
    filename: 'send-email-recipients-template.csv',
    content: ['name,port', 'MV Sea Pearl,Nhava Sheva (JNPT)', 'MT Ocean Glory,Mundra Port'].join('\n'),
  },
];

function downloadCsvTemplate({ filename, content }) {
  const blob = new Blob([`${content}\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function Help() {
  const { emojify } = useEmoji();
  const extensionDriveUrl = (process.env.REACT_APP_EXTENSION_DRIVE_URL || '').trim();

  return (
    <div className="data-page help-page">
      <PageBreadcrumb current={emojify('❓', 'Help')} />

      <section className="data-page__header">
        <div className="data-page__intro">
          <h2>{emojify('❓', 'Help')}</h2>
          <p className="data-page__subtitle">
            How to set up senders, use the extension, and send emails.
          </p>
        </div>
      </section>

      <nav className="help-page__toc" aria-label="Help topics">
        {SECTIONS.map((section) => (
          <a key={section.id} href={`#${section.id}`}>
            {section.title}
          </a>
        ))}
      </nav>

      <div className="help-page__content">
        <section id="get-started" className="help-page__section">
          <h3>Get started</h3>
          <p>Use the app in this order:</p>
          <ol>
            <li>
              <strong>Ports</strong> — add or import the ports you work with.
            </li>
            <li>
              <strong>Vessels</strong> — add or import vessels (name + email). Recipients are matched
              from this list.
            </li>
            <li>
              <strong>Emails</strong> — add sender accounts (Gmail or GoDaddy) and confirm they have
              sends left.
            </li>
            <li>
              <strong>Send Email</strong> — upload recipients, choose when to send, pick senders,
              write the message, then review and send.
            </li>
          </ol>
        </section>

        <section id="email-providers" className="help-page__section">
          <h3>Email providers</h3>
          <p>
            Add sender accounts under <strong>Emails</strong>. Only <strong>Gmail</strong> and{' '}
            <strong>GoDaddy</strong> are supported.
          </p>

          <h4>Gmail</h4>
          <p>Gmail needs an <strong>App Password</strong> (not your normal login password).</p>
          <ol>
            <li>Turn on 2-Step Verification for the Google account.</li>
            <li>
              Open{' '}
              <a
                href="https://myaccount.google.com/apppasswords"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google App Passwords
              </a>
              .
            </li>
            <li>Create an app password (e.g. “EKDANTAYA”) and copy the 16-character password.</li>
            <li>
              In the app go to <strong>Emails</strong> → <strong>Add</strong>.
            </li>
            <li>
              Choose provider <strong>Gmail</strong>, enter a display name, the Gmail address, and
              the app password, then save.
            </li>
          </ol>

          <h4>GoDaddy</h4>
          <ol>
            <li>Use the email address hosted on GoDaddy (or your custom domain via GoDaddy).</li>
            <li>Use the mailbox password from GoDaddy / Workspace Email.</li>
            <li>
              In the app go to <strong>Emails</strong> → <strong>Add</strong>.
            </li>
            <li>
              Choose provider <strong>GoDaddy</strong>, enter a display name, the email, and
              password, then save.
            </li>
            <li>
              If verify fails, confirm the mailbox password and that SMTP is allowed for that
              account.
            </li>
          </ol>
        </section>

        <section id="csv-templates" className="help-page__section">
          <h3>CSV templates</h3>
          <p>Download sample CSV files for imports and Send Email recipients.</p>
          <div className="help-page__downloads">
            {CSV_TEMPLATES.map((template) => (
              <button
                key={template.id}
                type="button"
                className="help-page__btn help-page__btn--secondary"
                onClick={() => downloadCsvTemplate(template)}
              >
                {template.label}
              </button>
            ))}
          </div>
          <ul className="help-page__template-notes">
            <li>
              <strong>Ports</strong> — columns: <code>name</code>, <code>city</code>
            </li>
            <li>
              <strong>Vessels</strong> — columns: <code>name</code>, <code>email</code>
            </li>
            <li>
              <strong>Emails</strong> — columns: <code>email</code>, <code>password</code>,{' '}
              <code>provider</code> (<code>gmail</code> or <code>godaddy</code>),{' '}
              <code>display_name</code>
            </li>
            <li>
              <strong>Send Email</strong> — columns: <code>name</code>, <code>port</code> (matched to
              vessels)
            </li>
          </ul>
        </section>

        <section id="extension" className="help-page__section">
          <h3>Browser extension</h3>
          <h4>Download</h4>
          <p>Download the extension ZIP, then follow the install steps below.</p>
          <p>
            {extensionDriveUrl ? (
              <a
                className="help-page__btn"
                href={extensionDriveUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download
              </a>
            ) : (
              <button type="button" className="help-page__btn" disabled>
                Download
              </button>
            )}
          </p>

          <h4>Install (Chrome / Edge)</h4>
          <ol>
            <li>Unzip the downloaded file.</li>
            <li>
              Open <code>chrome://extensions</code> (or <code>edge://extensions</code>).
            </li>
            <li>
              Turn on <strong>Developer mode</strong>.
            </li>
            <li>
              Click <strong>Load unpacked</strong> and select the unzipped extension folder.
            </li>
          </ol>

          <h4>Use</h4>
          <ol>
            <li>Open the site or page the extension is built for.</li>
            <li>Use the extension to extract vessel / port data.</li>
            <li>
              Import that CSV into <strong>Vessels</strong> or <strong>Ports</strong> as needed.
            </li>
          </ol>
        </section>

        <section id="send-emails" className="help-page__section">
          <h3>Send emails</h3>
          <ol>
            <li>
              Go to <strong>Send Email</strong>.
            </li>
            <li>
              Upload a recipients CSV with <strong>name</strong> and <strong>port</strong> columns.
              Matched vessels are shown as Found. Use the sample under{' '}
              <a href="#csv-templates">CSV templates</a>.
            </li>
            <li>
              Choose <strong>Send now</strong> or <strong>Schedule later</strong> (quota is checked for
              that time).
            </li>
            <li>
              Choose senders (auto or manual). The plan shows how many sends each account will use.
            </li>
            <li>Write the subject and message; attach files if needed.</li>
            <li>
              Review the summary, then click <strong>Send Email</strong> or{' '}
              <strong>Schedule Email</strong>.
            </li>
          </ol>
        </section>

        <section id="scheduled-logs" className="help-page__section">
          <h3>Scheduled emails &amp; logs</h3>
          <ul>
            <li>
              <strong>Scheduled Emails</strong> — view waiting groups, open details, reschedule, or
              cancel.
            </li>
            <li>
              <strong>Email Logs</strong> — see sent and failed messages after delivery attempts.
            </li>
            <li>
              <strong>Dashboard</strong> — quick counts for sends left, waiting groups, and recent
              sent volume.
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default Help;
